// Anonymous, consent-gated page-view tracker. Fires on route change.
// Also broadcasts on the live-visitors presence channel for the admin "Live now" widget.
import { useEffect, useRef, useState } from "react";
import { useLocation } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { SUPABASE_PUBLISHABLE_KEY, TRACKING_PROXY_URL } from "@/integrations/supabase/config";
import { getConsent } from "@/lib/consent";
import { logger } from "@/lib/logger";
import { isTrackableBrowserVisit } from "@/lib/analyticsHost";
import { sanitizeTrackedPath } from "../../supabase/functions/_shared/trackedPath";
import { PRESENCE_HEARTBEAT_MS } from '@/lib/livePresence';

const SESSION_KEY = "visitor-session-id";

function getOrCreateSessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

export function usePageTracking() {
  const location = useLocation();
  const lastPathRef = useRef<string | null>(null);
  const pathRef = useRef<string | null>(null);
  const [consent, setCurrentConsent] = useState(getConsent);
  const path = sanitizeTrackedPath(location.pathname + location.search);
  pathRef.current = path;
  const eligible = isTrackableBrowserVisit() && path !== null && consent === 'accepted';

  useEffect(() => {
    const update = () => setCurrentConsent(getConsent());
    window.addEventListener('consent-changed', update);
    window.addEventListener('storage', update);
    return () => { window.removeEventListener('consent-changed', update); window.removeEventListener('storage', update); };
  }, []);

  useEffect(() => {
    // The Lovable editor and preview hosts are not visitors (2026-10-14: they
    // were two-thirds of all rows). See src/lib/analyticsHost.ts.
    if (!eligible) { lastPathRef.current = null; return; }
    // Portal paths are not tracked, token segments are replaced and the query
    // string is reduced to utm_* -- this path is stored AND broadcast on a
    // public presence channel, so it must never carry a capability token.
    if (path === null) return;
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;

    const session_id = getOrCreateSessionId();
    const referrer = document.referrer && !document.referrer.includes(window.location.host)
      ? document.referrer
      : null;

    // Routed through the Cloudflare proxy (track.akiva.com) so the CF-IP*
    // visitor-location headers reach the edge function; direct calls to
    // Supabase bypass Cloudflare and record no location. The publishable key
    // is a public browser credential; consent gating and rate limits stay
    // server-side.
    void fetch(TRACKING_PROXY_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ session_id, path, referrer, consent_given: true }),
    }).then((response) => {
      if (!response.ok) logger.warn('page-tracking rejected', { status: response.status });
    }).catch((err) => logger.warn("page-tracking failed", { err }));

  }, [path, eligible]);

  useEffect(() => {
    if (!eligible) return;
    let subscribed = false;
    let disposed = false;
    let lastActive = Date.now();
    const channel = supabase.channel('live-visitors', { config: { presence: { key: getOrCreateSessionId() } } });
    const publish = () => {
      const currentPath = pathRef.current;
      if (!subscribed || disposed || currentPath === null || getConsent() !== 'accepted') return;
      // No device GPS, identity, or historical-location substitution.
      void channel.track({ path: currentPath, country: null, last_seen: Date.now(), last_active: lastActive, visible: document.visibilityState === 'visible' })
        .catch((err) => logger.warn('visitor presence failed', { err }));
    };
    const activity = () => { lastActive = Date.now(); };
    const visibility = () => { if (document.visibilityState === 'visible') activity(); publish(); };
    channel.subscribe((status) => { subscribed = status === 'SUBSCRIBED'; if (subscribed) publish(); });
    const interval = window.setInterval(publish, PRESENCE_HEARTBEAT_MS);
    for (const event of ['pointerdown', 'keydown', 'scroll']) window.addEventListener(event, activity, { passive: true });
    document.addEventListener('visibilitychange', visibility);
    return () => {
      disposed = true;
      window.clearInterval(interval);
      for (const event of ['pointerdown', 'keydown', 'scroll']) window.removeEventListener(event, activity);
      document.removeEventListener('visibilitychange', visibility);
      void supabase.removeChannel(channel);
    };
  }, [eligible]);
}

